import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown,
  BadgeCheck,
  ShieldCheck,
  Banknote,
  Globe,
  Gift,
  Home,
  TrendingUp,
  ArrowRight,
  Lock,
  Sparkles,
  Copy,
  Check,
} from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { goToAppStore, API } from "../lib/api";

const REF_KEY = "ss_stylist_ref";
const VIDEO_SRC = `${API}/media/how-it-works-stylists`;

// Founding seat scarcity (display figures, bump as seats fill).
const SEATS_CLAIMED = 42;
const SEATS_TOTAL = 256;

// Ambassador registry, keyed by unique reference code. Add new ambassadors here.
const AMBASSADORS = {
  "AMB-2J2G2PDQ": {
    name: "Jodi Quinton",
    title: "Head of Network Sales",
    photo: "/ambassadors/jodi-quinton.jpg",
  },
};

const GENERIC_AMBASSADOR = {
  name: "your Senior Stylist Ambassador",
  title: "Senior Stylist Network",
  photo: "",
};

const BENEFITS = [
  { icon: Globe, text: "Minimum 115 clients globally, ready to consult." },
  { icon: Gift, text: "£0 platform fee for life as a founding community member." },
  { icon: Home, text: "Work from anywhere with zero salon overheads, on your own hours." },
  { icon: TrendingUp, text: "Live earnings dashboard with monthly payouts." },
];

const reveal = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
};

export default function StylistInvite() {
  const { handle } = useParams();
  const [params] = useSearchParams();

  // Resolve referral code from path param or ?ref=, then persist for the whole flow.
  const ref = useMemo(() => {
    const fromQuery = params.get("ref");
    const code = (handle || fromQuery || "").trim();
    return code;
  }, [handle, params]);

  useEffect(() => {
    if (ref) {
      try {
        localStorage.setItem(REF_KEY, ref);
        sessionStorage.setItem(REF_KEY, ref);
      } catch (e) { /* storage unavailable, attribution best-effort */ }
      trackEvent("stylist_invite_view", ref);
    }
    document.title = "You are invited | Senior Stylist";
  }, [ref]);

  const stored = (() => {
    try { return localStorage.getItem(REF_KEY) || ""; } catch { return ""; }
  })();
  const activeRef = ref || stored;

  const ambassador =
    AMBASSADORS[(activeRef || "").toUpperCase()] || GENERIC_AMBASSADOR;

  const seatsPct = Math.round((SEATS_CLAIMED / SEATS_TOTAL) * 100);

  const [copied, setCopied] = useState(false);
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText((activeRef || "").toUpperCase());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard unavailable */ }
  };

  const claim = () => {
    trackEvent("stylist_apply_click", activeRef || "direct");
    goToAppStore();
  };

  return (
    <div className="min-h-screen bg-stylist-ink text-white" data-testid="stylist-invite-page">
      <div className="relative mx-auto max-w-md px-5 pt-8 pb-16 overflow-hidden">
        {/* ambient glow */}
        <div className="pointer-events-none absolute -top-24 right-0 w-72 h-72 rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, rgba(197,155,39,0.5), transparent 70%)" }} />

        {/* wordmark */}
        <div className="relative flex items-center justify-center">
          <Link to="/" className="font-serif text-xl tracking-tight text-white" data-testid="invite-logo">
            Senior <span className="text-stylist-gold">Stylist</span>
          </Link>
        </div>

        {/* VIP pill */}
        <motion.div {...reveal} className="relative mt-7 flex justify-center">
          <span
            className="inline-flex items-center gap-2 rounded-full border border-stylist-gold/40 bg-stylist-gold/10 px-4 py-2 text-[0.7rem] font-mono uppercase tracking-[0.22em] text-stylist-gold"
            data-testid="invite-vip-pill"
          >
            <Crown className="w-3.5 h-3.5" />
            VIP Invitation · By Introduction Only
          </span>
        </motion.div>

        {/* headline */}
        <motion.h1
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.05 }}
          className="relative mt-6 font-serif text-4xl leading-[1.08] tracking-tight"
          data-testid="invite-headline"
        >
          Monetize Your Hair Advice Beyond the Salon Chair
        </motion.h1>

        {/* ambassador photo */}
        {ambassador.photo && (
          <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.08 }} className="relative mt-7 flex justify-center">
            <div className="rounded-full p-[3px] bg-gradient-to-b from-stylist-gold to-stylist-gold/30">
              <img
                src={ambassador.photo}
                alt={ambassador.name}
                className="h-24 w-24 rounded-full object-cover"
                data-testid="invite-ambassador-photo"
              />
            </div>
          </motion.div>
        )}

        {/* introduced by */}
        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.1 }}
          className="relative mt-5 flex items-start gap-3 rounded-2xl border border-stylist-gold/25 bg-white/[0.03] p-4"
          data-testid="invite-ambassador-intro"
        >
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stylist-gold/12">
            <BadgeCheck className="h-4.5 w-4.5 text-stylist-gold" />
          </span>
          <p className="text-sm leading-relaxed text-white/85">
            Personally introduced by <span className="font-semibold text-white">{ambassador.name}</span>
            {ambassador.title ? `, ${ambassador.title}.` : "."}
          </p>
        </motion.div>

        {/* body */}
        <motion.p
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.12 }}
          className="relative mt-6 text-base leading-relaxed text-white/70"
        >
          Stop answering community questions for free. Claim 1 of {SEATS_TOTAL} complimentary Founding
          Stylist passes to open your verified digital chair on Senior Stylist.
        </motion.p>

        <motion.p
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.14 }}
          className="relative mt-4 text-sm italic text-stylist-gold/90"
        >
          This founding seat is offered by invitation only, not open to the public.
        </motion.p>

        {/* scarcity counter */}
        <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.16 }} className="relative mt-5" data-testid="invite-seats">
          <p className="text-sm font-medium text-white/85">
            <span className="text-stylist-gold font-semibold">Claimed: {SEATS_CLAIMED} / {SEATS_TOTAL}</span>{" "}
            Free Founding Seats Taken
          </p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-stylist-gold"
              style={{ width: `${seatsPct}%` }}
              data-testid="invite-seats-bar"
            />
          </div>
          <p className="mt-2 text-xs text-white/45">
            Once all {SEATS_TOTAL} spots are claimed, standard pro platform fees apply.
          </p>
        </motion.div>

        {/* video walkthrough */}
        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.18 }}
          className="relative mt-7 rounded-2xl overflow-hidden border border-white/12 aspect-video bg-black"
          data-testid="invite-video-walkthrough"
          style={{ boxShadow: "0 0 60px rgba(197,155,39,0.12)" }}
        >
          <video
            className="absolute inset-0 w-full h-full object-contain"
            controls
            playsInline
            preload="auto"
            data-testid="invite-video-player"
          >
            <source src={VIDEO_SRC} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </motion.div>
        <p className="relative mt-3 text-center text-xs text-white/40">
          See how Senior Stylist turns 10 minutes of downtime into professional income from your phone.
        </p>

        {/* referral code, invite only */}
        {activeRef && (
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.19 }}
            className="relative mt-6 rounded-2xl border border-stylist-gold/40 bg-stylist-gold/[0.07] p-5 text-center"
            data-testid="invite-ref-code-block"
          >
            <p className="text-sm font-medium text-white leading-relaxed">
              This process is invite only. Please use the referral code below when you sign up.
            </p>
            <div className="mt-3 flex items-center justify-center gap-3">
              <span
                className="font-mono text-lg font-semibold tracking-[0.15em] text-stylist-gold"
                data-testid="invite-ref-code"
              >
                {activeRef.toUpperCase()}
              </span>
              <button
                onClick={copyCode}
                data-testid="invite-ref-copy"
                className="inline-flex items-center gap-1.5 rounded-full border border-stylist-gold/40 px-3 py-1.5 text-xs font-medium text-stylist-gold transition-colors hover:bg-stylist-gold hover:text-stylist-ink"
              >
                {copied ? (
                  <><Check className="w-3.5 h-3.5" /> Copied</>
                ) : (
                  <><Copy className="w-3.5 h-3.5" /> Copy</>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* primary CTA under video */}
        <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.2 }} className="relative mt-6">
          <button
            onClick={claim}
            data-testid="invite-apply-cta"
            className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-stylist-gold px-7 py-4 text-base font-semibold text-stylist-ink transition-colors duration-300 hover:bg-white"
          >
            Claim My Founding Seat
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-2.5 flex items-center justify-center gap-1.5 text-xs text-white/45">
            <Lock className="w-3 h-3" />
            Opens the Senior Stylist app, then enter your referral code to complete sign up
          </p>
        </motion.div>

        {/* what you get */}
        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.12 }}
          className="relative mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6"
          data-testid="invite-what-you-get"
        >
          <h2 className="font-serif text-2xl">What you get</h2>
          <ul className="mt-5 space-y-4">
            {BENEFITS.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-3"
                data-testid={`invite-benefit-${i + 1}`}
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stylist-gold/12">
                  <b.icon className="h-4 w-4 text-stylist-gold" />
                </span>
                <span className="pt-1 text-sm leading-relaxed text-white/80">{b.text}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* trust badges */}
        <div className="relative mt-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-white/50">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-stylist-gold" /> NVQ Level 2 required</span>
          <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-stylist-gold" /> 5 years experience</span>
          <span className="inline-flex items-center gap-1.5"><Banknote className="w-3.5 h-3.5 text-stylist-gold" /> Fast payouts</span>
        </div>

        {/* bottom CTA */}
        <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.3 }} className="relative mt-10 rounded-3xl border border-stylist-gold/25 bg-gradient-to-b from-white/[0.04] to-transparent p-6 text-center">
          <Sparkles className="mx-auto h-6 w-6 text-stylist-gold" />
          <p className="mt-3 font-serif text-xl leading-snug">Your founding seat is waiting.</p>
          <p className="mt-1 text-sm text-white/60">It takes a few minutes to claim.</p>
          <button
            onClick={claim}
            data-testid="invite-apply-cta-bottom"
            className="group mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-stylist-gold px-7 py-4 text-base font-semibold text-stylist-ink transition-colors duration-300 hover:bg-white"
          >
            Claim My Founding Seat
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </div>
  );
}
